import { useQuery } from '@apollo/client';
import React from 'react';
import DismissableInlineMessage from '../../dismissable-inline-message';
import Icon from '../../icon/src/icons';
import { ColGroup, SearchResultsTable, TableBody, TableHead, SearchResultColumn } from '../../search-results-table';
import './cloud-subscription-list.scss';
import CloudSubscriptionRow from './cloud-subscription-row';
import { CloudSubscriptionSearchParams, CloudSubscription } from '@atlassian/cssxp-typescommon';
import {
  CloudListDetailsQuery,
  fetchCloudSubscriptionDetailsForContact,
} from './queries/fetch-cloud-subscription-details';
import { SortDirection } from '@css-xp/graphtypes';
import { ContactSearchOptions } from '@atlassian/cssxp-types-contact';

interface ComponentProps {
  withBitbucketFreeFilter?: boolean;
  params: CloudSubscriptionSearchParams;
  newURL?: boolean;
}

const RoleDisclaimer = () => (
  <DismissableInlineMessage
    position="left-end"
    renderMessage={() => (
      <p className="inline-message" style={{ width: '400px', whiteSpace: 'break-spaces' }}>
        Currently we can only return <strong>User</strong> and <strong>Site Admin</strong> roles. To see if a contact is
        a billing or technical contact, please check the cloud site page.
      </p>
    )}
    renderTitle={(toggleMessage: () => void) => (
      <span className="hoverable-icon" onClick={toggleMessage}>
        <Icon size="small" type="info" />
      </span>
    )}
  />
);

export const PAGE_TAB_SEARCH_RESULTS_LOAD_MORE = 15;

export const searchOptions: ContactSearchOptions = {
  size: PAGE_TAB_SEARCH_RESULTS_LOAD_MORE,
  from: 0,
  sort: {
    sortFields: ['createdDate', 'id'],
    sortFieldsDirections: [SortDirection.DESC, SortDirection.ASC],
  },
};

export const getCloudSubscriptionList = (data?: {
  cloudSubscription?: { totalnumber?: number; results?: [CloudSubscription] };
}) => data?.cloudSubscription?.results || [];
export const getTotalHits = (data?: { cloudSubscription?: { totalHits?: number } }) =>
  data?.cloudSubscription?.totalHits;
export const getEmail = (email: string) => (email ? decodeURIComponent(email) : null);

export default function CloudSubscriptionList({ params, newURL }: ComponentProps) {
  const { fetchMore, data, loading, error } = useQuery(fetchCloudSubscriptionDetailsForContact, {
    variables: {
      searchOptions,
      searchParams: {
        query: params.query,
        ...(params.email && { email: params.email }),
      },
    },
  });

  const handleLoadMoreClick = () => {
    fetchMore({
      variables: {
        searchOptions,
        searchParams: {
          query: params.query,
        },
      },
      //@ts-ignore
      updateQuery: (previousResult, { fetchMoreResult }: { fetchMoreResult: CloudListDetailsQuery }) => {
        if (fetchMoreResult?.cloudSubscription?.results && previousResult?.cloudSubscription?.results) {
          fetchMoreResult.cloudSubscription.results = [
            ...previousResult.cloudSubscription.results,
            ...fetchMoreResult.cloudSubscription.results,
          ];
        }
        return fetchMoreResult;
      },
    });
  };

  const cloudSubscriptionList = getCloudSubscriptionList(data);
  const totalCloudSubsList: number | undefined = getTotalHits(data);
  const hasNoResults = !error && cloudSubscriptionList.length === 0;
  const showLoadMore =
    cloudSubscriptionList && totalCloudSubsList ? totalCloudSubsList > cloudSubscriptionList?.length : false;

  const isRoles = cloudSubscriptionList
    .map((cloudList: any) => {
      return cloudList.roles;
    })
    .filter((role: any) => Boolean(role));

  const includeRoles: boolean = isRoles.length > 0;

  const getColumns = () => {
    const columns: SearchResultColumn[] = [
      {
        name: 'Site / Product',
        customClassNames: includeRoles ? 'col-30' : 'col-40',
      },
      {
        name: 'Active User',
        customClassNames: 'col-10',
      },
      {
        name: includeRoles ? 'Role' : undefined,
        customClassNames: includeRoles ? 'col-10' : undefined,
        renderComponent: <th>Role {includeRoles && <RoleDisclaimer />}</th>,
      },
      {
        name: 'ID',
        customClassNames: 'col-15',
      },
      {
        name: 'Page name',
        customClassNames: 'col-15',
      },
      {
        name: 'Renewal',
        customClassNames: 'col-10',
      },
      {
        name: 'Next bill / Expiry',
        customClassNames: !includeRoles ? 'col-10' : 'col-15',
      },
      {
        name: 'Product Edition',
        customClassNames: 'col-15',
      },
      {
        name: 'Bill to company',
        customClassNames: 'col-15',
      },
      {
        name: 'Ship to company',
        customClassNames: 'col-15',
      },
      {
        name: ' ',
      },
    ];
    columns.push({ name: ' ' });
    return columns;
  };

  return (
    <div data-test-id="cloud-subscription-list">
      <SearchResultsTable
        id="cloudSubscription"
        isLoading={loading}
        error={error?.toString()}
        heading={'Cloud subscription'}
        noResults={hasNoResults}
        loadMore={{
          showLoadMore,
          totalCount: totalCloudSubsList,
          resultsCount: cloudSubscriptionList.length,
          onLoadMoreClick: handleLoadMoreClick,
        }}
        entityName="cloud subscription"
      >
        <ColGroup columns={getColumns()} />
        <TableHead columns={getColumns()} />
        <TableBody>
          {cloudSubscriptionList.map((cloudSubscription: CloudSubscription) => (
            <CloudSubscriptionRow
              {...cloudSubscription}
              includeRoles={includeRoles}
              key={cloudSubscription.businessKey}
              newURL={newURL}
            />
          ))}
        </TableBody>
      </SearchResultsTable>
    </div>
  );
}
